import React, { Component, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { StandardButton } from './StandardButton';
import { logger } from '../../services/LoggingService';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, errorInfo: React.ErrorInfo, reset: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('Error caught by ErrorBoundary', 'ErrorBoundary', {
      componentStack: errorInfo.componentStack
    }, error);
    this.setState({ errorInfo });
  }

  reset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback(
          this.state.error!,
          this.state.errorInfo!,
          this.reset
        );
      }

      return <DefaultErrorFallback error={this.state.error!} reset={this.reset} />;
    }

    return this.props.children;
  }
}

interface DefaultErrorFallbackProps {
  error: Error;
  reset: () => void;
}

const DefaultErrorFallback: React.FC<DefaultErrorFallbackProps> = ({ error, reset }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-primary to-dark-secondary flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl w-full"
      >
        <div className="bg-white/5 backdrop-blur-lg rounded-2xl border border-white/10 p-8 shadow-2xl">
          {/* Error Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center">
              <svg
                className="w-10 h-10 text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>

          {/* Error Message */}
          <h1 className="text-2xl font-semibold text-white text-center mb-4">
            Something went wrong
          </h1>
          <p className="text-gray-300 text-center mb-6">
            We encountered an unexpected error. Please try refreshing the page or contact support if the problem persists.
          </p>

          {/* Error Details (Development only) */}
          {process.env.NODE_ENV === 'development' && (
            <details className="mb-6">
              <summary className="cursor-pointer text-sm text-gray-400 hover:text-gray-300 transition-colors">
                Show error details
              </summary>
              <div className="mt-4 p-4 bg-black/20 rounded-lg overflow-auto">
                <pre className="text-xs text-red-400 whitespace-pre-wrap">
                  {error.toString()}
                  {error.stack && '\n\nStack trace:\n' + error.stack}
                </pre>
              </div>
            </details>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <StandardButton
              onClick={() => window.location.reload()}
              variant="secondary"
              size="large"
            >
              Refresh Page
            </StandardButton>
            <StandardButton
              onClick={reset}
              variant="primary"
              size="large"
            >
              Try Again
            </StandardButton>
          </div>

          {/* Support Link */}
          <p className="text-sm text-gray-400 text-center mt-8">
            Need help?{' '}
            <button
              onClick={() => logger.info('Contact support clicked', 'ErrorBoundary')}
              className="text-professional-blue-400 hover:text-professional-blue-300 underline transition-colors"
            >
              Contact Support
            </button>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

// Specialized error boundaries for different parts of the app
export const ScenarioErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <ErrorBoundary
      fallback={(error, errorInfo, reset) => (
        <div className="p-8 bg-red-500/10 border border-red-500/20 rounded-lg">
          <h2 className="text-xl font-semibold text-red-400 mb-2">
            Scenario Loading Error
          </h2>
          <p className="text-gray-300 mb-4">
            We couldn't load this scenario. This might be a temporary issue.
          </p>
          <StandardButton
            onClick={reset}
            variant="danger"
          >
            Retry Loading
          </StandardButton>
        </div>
      )}
    >
      {children}
    </ErrorBoundary>
  );
};